import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ContinentsEntity } from '@firebird-web/continents-store';
import { getLocationDetailsFavoriteLists } from '@firebird-web/custom-locations-store';
import { continents, REGION_LABELS } from '@firebird-web/shared-constants';
import { LocalStorageService } from '@firebird-web/shared-services';
import { CommonUtils } from '@firebird-web/shared-utils';
import { Store } from '@ngrx/store';
import { LocalStorageKeysEnum } from 'libs/shared/enums';
import { LocationService } from 'libs/shared/services/src/lib/location.service';
import { isEqual, isNull, first as lodashFirst } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  FavoriteCity,
  FavoriteList,
} from '../../../../../../custom-locations-store/src/lib/models/favorite-locations.types';
import { City, IContinent, IRegion } from '../location.interface';
import {
  CityLocationSelectionDialogData,
  CityLocationSelectionDialogResult,
} from '../location.types';
import {
  AGGREGATED_SUFFIX,
  ALL_REGIONS_PREFIX,
  EIA_REGIONS_PREFIX,
  REGIONS_KEYWORD,
  SPECIAL_CITIES,
} from './city-location-selection.constants';
import { CityLocationSelectionService } from './city-location-selection.service';

@Component({
  selector: 'firebird-web-city-location-selection',
  templateUrl: './city-location-selection.component.html',
  styleUrls: ['./city-location-selection.component.scss'],
  providers: [CityLocationSelectionService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CityLocationSelectionComponent implements OnInit, OnDestroy {
  public readonly allCitiesOptionKey =
    this.cityLocationSelectionService.allCitiesOptionKey;
  public customLocationLists$ = this.store.select(
    getLocationDetailsFavoriteLists
  );
  public continentList$ = this.locService.continents$;
  public regionList$ = new BehaviorSubject<IRegion[]>([]);
  public selectedCustomLocationList$ = new BehaviorSubject<FavoriteList | null>(
    null
  );
  public selectedContinent$ = new BehaviorSubject<IContinent | null>(null);
  public selectedRegion$ = new BehaviorSubject<string | null>(null);
  public customLocationList: FavoriteCity[][] = [];
  public cityList: City[][] = [];
  public isMaps = false;
  public isLoading = false;
  public isLocationPinned = this.locService.isPinned();
  private isRegionInitialized = false;
  private destroy$ = new Subject<boolean>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data: CityLocationSelectionDialogData,
    private readonly dialogRef: MatDialogRef<
      CityLocationSelectionComponent,
      CityLocationSelectionDialogResult
    >,
    private readonly store: Store,
    private readonly cdr: ChangeDetectorRef,
    private readonly locService: LocationService,
    private router: Router,
    private readonly cityLocationSelectionService: CityLocationSelectionService,
    private readonly localStorageService: LocalStorageService
  ) {}

  public get isCustomLocationSelected(): boolean {
    return !!this.selectedCustomLocationList;
  }

  public get isWidget(): boolean {
    return !!this.data?.isWidget;
  }

  private get initialSiteId(): string {
    return this.data?.siteId ?? '';
  }

  private get initialCustomLocationId(): string | number | undefined {
    return this.data?.customList?.id;
  }

  private get selectedCustomLocationList(): FavoriteList | null {
    return this.selectedCustomLocationList$.getValue();
  }

  public ngOnInit(): void {
    this.initLocation();
    this.subscribeToGetCityList();
    this.subscribeToGetCustomLocationList();
    this.isMaps = this.router.url.includes('maps');
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.regionList$.complete();
    this.selectedCustomLocationList$.complete();
    this.selectedContinent$.complete();
    this.selectedRegion$.complete();
    this.cityLocationSelectionService.clearCache();
  }

  public onCustomLocationChange(customLocation: FavoriteList): void {
    this.selectedCustomLocationList$.next(customLocation);
  }

  public onContinentChange({ id, text: name }: ContinentsEntity): void {
    this.setContinentAndRegion(of({ id, name }));
    this.selectedCustomLocationList$.next(null);
  }

  public onRegionChange(siteId: string | null): void {
    this.selectedRegion$.next(siteId);
  }

  public onCustomLocationSelected({
    siteId,
    continent,
    displayName,
    region,
  }: FavoriteCity): void {
    const [city] = displayName.split(',');

    this.onCitySelected({
      city,
      siteId,
      region,
      continent,
      displayName,
    } as City);
  }

  public onCitySelected({
    siteId,
    region,
    city,
    continent,
    displayName,
  }: City): void {
    const selectedCustomLocationList = this.selectedCustomLocationList;
    const selectedRegion = this.selectedRegion$.getValue();
    const effectiveRegion =
      selectedRegion === REGION_LABELS.ISO_REGIONS ||
      selectedRegion === REGION_LABELS.EIA_REGIONS
        ? selectedRegion
        : region;

    this.locService
      .findContinentByIdAsync(continent)
      .pipe(first())
      .subscribe((continentName = '') => {
        this.closeDialog({
          city,
          region: effectiveRegion,
          siteId,
          continent,
          continentName,
          displayName,
          ...(selectedCustomLocationList && {
            customList: {
              id: selectedCustomLocationList.id,
              name: selectedCustomLocationList.listName,
              continent: selectedCustomLocationList.continent,
              continents: selectedCustomLocationList.continents,
            },
          }),
        });
      });
  }

  public isCityActive({ siteId }: City): boolean {
    return this.initialSiteId === siteId && !this.initialCustomLocationId;
  }
  public locationPinned(): void {
    const firstCity = this.getSelectedCity(this.initialSiteId);
    this.isLocationPinned = !this.isLocationPinned;
    this.localStorageService.setValue(
      LocalStorageKeysEnum.IsLocationPinned,
      JSON.stringify(this.isLocationPinned)
    );
    this.locService.isPinnedChange.next(this.isLocationPinned);
    this.onCitySelected(firstCity);
  }

  public getSelectedCity(siteId: string) {
    return (
      this.cityList.flat().find((city) => city.siteId === siteId) ??
      this.cityList[0][0]
    );
  }

  public isCustomLocationActive({
    siteId,
    profileListId,
  }: FavoriteCity): boolean {
    return (
      this.initialSiteId === siteId &&
      this.initialCustomLocationId === profileListId
    );
  }

  private initLocation(): void {
    const customListId = this.initialCustomLocationId;

    if (customListId) {
      this.customLocationLists$
        .pipe(
          first(),
          map((customLocationLists) =>
            customLocationLists.find(({ id }) => id === customListId)
          ),
          filter(
            (customLocationList): customLocationList is FavoriteList =>
              !!customLocationList
          )
        )
        .subscribe((customLocationList) => {
          this.selectedCustomLocationList$.next(customLocationList);
          this.isRegionInitialized = true;
        });

      return;
    }

    this.setContinentAndRegion(this.extractContinentFromDialogData());
  }

  private subscribeToGetCityList(): void {
    combineLatest([this.selectedContinent$, this.selectedRegion$])
      .pipe(
        filter((location): location is [IContinent, string] =>
          location.every((item) => !isNull(item))
        ),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        tap(([{ id }, region]) => {
          if (
            !this.cityLocationSelectionService.isCityListInCache(
              id as string,
              region
            )
          ) {
            this.isLoading = true;
            this.cdr.markForCheck();
          }
        }),
        switchMap(([continent, region]) =>
          this.cityLocationSelectionService.getCityList(continent, region)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((cityList) => {
        this.cityList = cityList;
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  private subscribeToGetCustomLocationList(): void {
    this.selectedCustomLocationList$
      .pipe(
        filter(
          (
            selectedCustomLocationList
          ): selectedCustomLocationList is FavoriteList =>
            !isNull(selectedCustomLocationList)
        ),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        map(({ sites }) => CommonUtils.toChunksByColumnsQty(sites, 3)),
        takeUntil(this.destroy$)
      )
      .subscribe((customLocationList) => {
        this.customLocationList = customLocationList;
      });
  }

  private setContinentAndRegion(continent$: Observable<IContinent>): void {
    continent$
      .pipe(
        first(),
        tap((continent) => this.selectedContinent$.next(continent)),
        switchMap((continent) =>
          this.cityLocationSelectionService.getRegionList(continent)
        )
      )
      .subscribe((regionList) => {
        this.regionList$.next(regionList);
        this.onRegionChange(
          this.isRegionInitialized
            ? (lodashFirst(regionList) as IRegion).regionName
            : this.extractRegionFromDialogData()
        );
        this.isRegionInitialized = true;
      });
  }

  private extractContinentFromDialogData(): Observable<IContinent> {
    const { continent: id, continentName } = this.data;

    return this.locService.findContinentByIdAsync(id).pipe(
      map((widgetContinentName) => ({
        id,
        name: this.isWidget ? widgetContinentName : continentName,
      }))
    );
  }

  private extractRegionFromDialogData(): string | null {
    const { siteId = null, region, city = null, continent } = this.data;

    if (region?.toUpperCase().startsWith(ALL_REGIONS_PREFIX)) {
      return REGION_LABELS.ALL_AGGREGATES;
    }

    if (
      region === REGION_LABELS.ISO_REGIONS ||
      region === REGION_LABELS.EIA_REGIONS
    ) {
      return region;
    }

    const isSpecialCity = !!city && SPECIAL_CITIES.includes(city);
    const isEia = region?.toUpperCase().startsWith(EIA_REGIONS_PREFIX);
    const isValidCity = !!city?.trim();
    const shouldUseCity =
      isSpecialCity ||
      (isEia && isValidCity && continent === continents.NORTH_AMERICA);

    if (city && shouldUseCity) {
      return city.replace(AGGREGATED_SUFFIX, '');
    }

    return region.toLowerCase().includes(REGIONS_KEYWORD) && siteId
      ? siteId.replace(/-FIRE$/, '').replace('_', ' ')
      : region;
  }

  private closeDialog(result?: CityLocationSelectionDialogResult): void {
    this.dialogRef.close(result);
  }
}
